/**
 * upload-preview/styles.ts — the Tool Upload journey, styled from the design.
 *
 * SOURCE OF EVERY NUMBER: design/Tool Upload (1).svg and P1-P6, measured with
 * .claude/skills/design-to-page/scripts/extract-geometry.mjs. The audit is at
 * docs/design-build/tool-upload/AUDIT.md and the copy at COPY.md.
 *
 * SELF-CONTAINED ON PURPOSE. This page does NOT load TOOLS_STYLES. That block styles bare `button`,
 * `th`/`td`, `input` and friends under `.tb-tools` at specificity (0,1,1), which silently beat every
 * single-class rule here — the first run rendered the design's outlined pills as solid accent buttons
 * and put vertical rules through the table. The design gives this journey its own form language, so it
 * carries its own complete styles and nothing has to fight for precedence.
 *
 * Colour, radius, type and control heights still come from the --tb-* tokens in App.tsx, which were
 * re-derived from this same design. Nothing here restates a token value.
 *
 * NO ABSOLUTE POSITIONING FOR LAYOUT. The frames are flat Figma exports carrying only canvas x/y, so
 * every position here was reconstructed as a flex/grid relationship: the modal's 65px stepper insets,
 * its 32px body padding, the 20px field rhythm, the 32px column gap, the 65px table-row pitch. The only
 * absolutely-positioned things are the ones the design genuinely overlays — the scrim and the card on
 * top of it, and the search field's leading icon.
 *
 * Measured geometry, for traceability:
 *   card            1056x640 rx16, --tb-bg on a --tb-scrim, 1px --tb-accent-ink border
 *   stepper         h32 pills, 65px inset each side, 94px between, then a 1px rule
 *   body columns    296px | 1fr split by a 1px rule (the frame's vertical divider at x=486)
 *   body padding    32px
 *   field rhythm    label (11px/500) + 6px + control (h32) + 20px to the next label
 *   two-up row      344px + 32px + 319px = 695px, the full field width
 *   table           993px wide, 65px row pitch, 2px --tb-accent-ink rule under the header
 *   footer          1px rule, then a 32px-inset row 26px below it
 */
export const UPLOAD_PREVIEW_STYLES = `
  /* Visually hidden but announced. The repo has no global sr-only utility, so this is scoped to the
     preview rather than introducing one (see .tb-search-status in App.tsx for the same technique). */
  .tb-up-sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  /* ── The page behind the modal (design/Tool Upload (1).svg and P6) ───────────────────────────── */
  .tb-up { padding: 0; }
  /* The frame draws this row at y=124: a bare label pill on the left, the strong action on the right. */
  .tb-up__bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 48px 0;
  }
  .tb-up__title {
    margin: 0;
    display: inline-flex;
    align-items: center;
    height: var(--tb-h);
    padding: 0 12px;
    font-size: var(--tb-fs);
    font-weight: var(--tb-fw);
    color: var(--tb-ink);
  }
  /* Publish is the frame's one --tb-accent-mid surface: the strong state, with accent-ink text. */
  .tb-up__publish {
    font: inherit;
    display: inline-flex;
    align-items: center;
    height: var(--tb-h);
    padding: 0 12px;
    border: none;
    border-radius: var(--tb-r);
    background: var(--tb-accent-mid);
    color: var(--tb-accent-ink);
    font-size: var(--tb-fs);
    font-weight: var(--tb-fw);
    cursor: pointer;
  }
  .tb-up__publish:hover { background: var(--tb-accent); }

  /* The uploads container: 948x325 rx8 with a --tb-accent-ink border at 20% in the frame. Height is a
     min, not a fix — the frame is a snapshot at one width and the card grid has to be able to grow. */
  .tb-up__panel {
    margin: 21px 48px 48px;
    min-height: 325px;
    padding: 16px;
    border: 1px solid var(--tb-accent-ink);
    border-color: color-mix(in srgb, var(--tb-accent-ink) 20%, transparent);
    border-radius: var(--tb-r);
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 230px));
    gap: 16px;
    align-content: start;
  }
  /* No empty state is drawn in frame 1 (an empty container only). UP-F46 requires a purposeful one, so
     this is an addition with no design precedent — logged in the audit. */
  .tb-up__empty {
    grid-column: 1 / -1;
    align-self: center;
    text-align: center;
    color: var(--tb-muted);
    font-size: var(--tb-fs);
  }

  /* ── The tool card (P6, and the Tool Preview panel in P2-P4 are the same component) ──────────── */
  .tb-up-card {
    border-radius: var(--tb-r);
    background: var(--tb-accent-soft);
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border: none;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  .tb-up-card:focus-visible { outline: 2px solid var(--tb-accent); outline-offset: 2px; }
  .tb-up-card__art {
    border-radius: var(--tb-r);
    background: var(--tb-bg);
    min-height: 128px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }
  .tb-up-card__meta { padding: 0 4px 4px; display: flex; flex-direction: column; gap: 6px; }
  .tb-up-card__name { font-size: var(--tb-fs-sm); font-weight: var(--tb-fw-label); }
  .tb-up-card__by { font-size: var(--tb-fs-sm); font-weight: var(--tb-fw); color: var(--tb-ink); }

  /* Connector tiles. The frames embed a 1280x960 raster of the real Outlook/Gmail/click/save marks;
     that art is DERIVED FROM THE SELECTED FLOW, so it is dynamic and must not be baked into source.
     These are deliberately plain placeholders — see the @wire marker in UploadPreview.tsx. */
  .tb-up-conn {
    width: 29px;
    height: 29px;
    border-radius: var(--tb-r);
    background: var(--tb-accent-soft);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: var(--tb-fs-sm);
    font-weight: var(--tb-fw-label);
    color: var(--tb-accent-ink);
  }

  /* ── The modal (P1-P5): a 40% scrim with the card centred on it ──────────────────────────────── */
  .tb-up-scrim {
    position: fixed;
    inset: 0;
    z-index: 50;
    background: var(--tb-scrim);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 24px 16px;
    overflow-y: auto;
  }
  .tb-up-modal {
    width: 100%;
    max-width: 1056px;
    min-height: 640px;
    margin-top: 207px;
    background: var(--tb-bg);
    border: 1px solid var(--tb-accent-ink);
    border-radius: var(--tb-r-lg);
    display: flex;
    flex-direction: column;
  }
  /* The frame centres the card at y=231 in a 1024-tall viewport. Above ~900px of viewport that offset
     reads as intended; below it the card would be pushed off-screen, so it collapses to the padding. */
  @media (max-height: 900px) {
    .tb-up-modal { margin-top: 0; }
  }

  /* ── Stepper ─────────────────────────────────────────────────────────────────────────────────── */
  .tb-up-steps {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    list-style: none;
    margin: 0;
    padding: 33px 65px;
    border-bottom: 1px solid var(--tb-accent-ink);
  }
  .tb-up-steps li {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    height: var(--tb-h);
    padding: 0 12px;
    border-radius: var(--tb-r);
    background: var(--tb-accent-soft);
    color: var(--tb-muted);
    font-size: var(--tb-fs);
    font-weight: var(--tb-fw);
    white-space: nowrap;
  }
  /* The active step is the frame's other --tb-accent-mid surface. Weight carries the state as well as
     colour, so it survives greyscale (PC-NFR01: never colour alone). */
  .tb-up-steps li[aria-current="step"] {
    background: var(--tb-accent-mid);
    color: var(--tb-accent-ink);
    font-weight: var(--tb-fw-label);
  }
  .tb-up-steps li[data-done="true"] { color: var(--tb-accent-ink); }

  /* ── Body: the frame's vertical divider becomes a two-column grid ────────────────────────────── */
  .tb-up-body {
    flex: 1 1 auto;
    display: grid;
    grid-template-columns: 296px 1fr;
    align-items: stretch;
  }
  .tb-up-body[data-single="true"] { grid-template-columns: 1fr; }
  .tb-up-aside {
    padding: 32px;
    border-right: 1px solid var(--tb-accent-ink);
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .tb-up-main { padding: 32px; display: flex; flex-direction: column; }
  /* Below the anchor width the two columns stack and the divider moves to the top edge. */
  @media (max-width: 900px) {
    .tb-up-body { grid-template-columns: 1fr; }
    .tb-up-aside { border-right: none; border-bottom: 1px solid var(--tb-accent-ink); }
  }

  .tb-up-aside__h { margin: 0; font-size: var(--tb-fs); font-weight: var(--tb-fw); }
  /* The identity panel: 230x161 rx8 accent-soft, 12px inset, label/value pairs. */
  .tb-up-ident {
    margin: 0 0 24px;
    padding: 12px;
    border-radius: var(--tb-r);
    background: var(--tb-accent-soft);
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .tb-up-ident div { display: flex; flex-direction: column; gap: 6px; }
  .tb-up-ident dt { font-size: var(--tb-fs-sm); font-weight: var(--tb-fw-label); }
  .tb-up-ident dd { margin: 0; font-size: var(--tb-fs-sm); font-weight: var(--tb-fw); }

  /* ── Fields ──────────────────────────────────────────────────────────────────────────────────── */
  .tb-up-fields { display: flex; flex-direction: column; gap: 20px; }
  /* 344 + 32 + 319 = 695 in the frame. Expressed as fractions so it holds at any width. */
  .tb-up-row { display: grid; grid-template-columns: 344fr 319fr; gap: 32px; }
  @media (max-width: 700px) {
    .tb-up-row { grid-template-columns: 1fr; }
  }
  .tb-up-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
  .tb-up-field > span { font-size: var(--tb-fs-sm); font-weight: var(--tb-fw-label); color: var(--tb-ink); }
  .tb-up-field input,
  .tb-up-field textarea,
  .tb-up-field select {
    font: inherit;
    font-size: var(--tb-fs);
    font-weight: var(--tb-fw);
    color: var(--tb-ink);
    height: var(--tb-h);
    padding: 0 12px;
    border: 1px solid var(--tb-accent-ink);
    border-color: color-mix(in srgb, var(--tb-accent-ink) 20%, transparent);
    border-radius: var(--tb-r);
    background: var(--tb-bg);
    width: 100%;
  }
  .tb-up-field textarea { height: 72px; padding: 8px 12px; resize: vertical; }
  .tb-up-field select { appearance: auto; }
  /* Focus has no precedent in the frames, which draw resting states only. Built from the accent
     tokens; a visible focus ring is not optional (PC-NFR01). Logged as an addition in the audit. */
  .tb-up-field :focus-visible { outline: 2px solid var(--tb-accent); outline-offset: 1px; }
  /* The three fields the design never drew but POST /api/tools requires. Marked so they are visibly
     an addition rather than silently blended in — see AUDIT decision 5. */
  .tb-up-field[data-added="true"] > span::after {
    content: " (not in design)";
    color: var(--tb-muted);
    font-weight: var(--tb-fw);
  }
  .tb-up-fields__half { max-width: 344px; }

  /* ── Footer ──────────────────────────────────────────────────────────────────────────────────── */
  .tb-up-foot {
    margin-top: auto;
    padding-top: 26px;
    border-top: 1px solid var(--tb-accent-ink);
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }
  .tb-up-foot__help { margin: 0; font-size: var(--tb-fs-sm); font-weight: var(--tb-fw); color: var(--tb-ink); }
  .tb-up-foot__help a { color: var(--tb-accent-ink); }
  .tb-up-foot__end { margin-left: auto; display: flex; gap: 16px; }
  /* The h23 small button from the Buttons sheet: rx8, a 30%-opacity accent-ink border, 11px text. */
  .tb-up-btn {
    font: inherit;
    display: inline-flex;
    align-items: center;
    height: var(--tb-h-sm);
    padding: 0 12px;
    border: 1px solid var(--tb-accent-ink);
    border-color: color-mix(in srgb, var(--tb-accent-ink) 30%, transparent);
    border-radius: var(--tb-r);
    background: transparent;
    color: var(--tb-ink);
    font-size: var(--tb-fs-sm);
    font-weight: var(--tb-fw);
    cursor: pointer;
    white-space: nowrap;
  }
  .tb-up-btn:hover { background: var(--tb-accent-soft); }
  .tb-up-btn:focus-visible { outline: 2px solid var(--tb-accent); outline-offset: 1px; }
  .tb-up-btn[disabled] { color: var(--tb-muted); cursor: not-allowed; }
  .tb-up-btn[disabled]:hover { background: transparent; }

  /* ── The picker (P1) ─────────────────────────────────────────────────────────────────────────── */
  .tb-up-pick { padding: 32px; display: flex; flex-direction: column; flex: 1 1 auto; }
  .tb-up-pick__h { margin: 5px 0 0; font-size: var(--tb-fs-lg); font-weight: var(--tb-fw); }
  .tb-up-pick__tools {
    margin: 22px 0 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  /* The modal's search field: h23, accent-soft, magnifier inset 12px. Absolute positioning here is
     the legitimate kind — an icon overlaid inside its own relative wrapper, not a canvas coordinate. */
  /* 792px of a 992px inner width in the frame, with the Filter control pushed to the far edge
     rather than sitting against the field. */
  .tb-up-search { position: relative; flex: 0 1 792px; }
  .tb-up-search input {
    font: inherit;
    width: 100%;
    height: var(--tb-h-sm);
    padding: 0 12px 0 35px;
    border: none;
    border-radius: var(--tb-r);
    background: var(--tb-accent-soft);
    color: var(--tb-ink);
    font-size: var(--tb-fs-sm);
    font-weight: var(--tb-fw);
  }
  .tb-up-search input:focus-visible { outline: 2px solid var(--tb-accent); outline-offset: 1px; }
  .tb-up-search svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 11px;
    height: 11px;
    color: var(--tb-icon);
    pointer-events: none;
  }

  /* The Automations table (design/Automations (1).svg): 65px row pitch, a 2px rule under the header,
     and a selection radio that appears only on hover or selection. */
  .tb-up-table { margin: 16px 0 0; width: 100%; border-collapse: collapse; font-size: var(--tb-fs); }
  .tb-up-table th {
    text-align: left;
    font-weight: var(--tb-fw);
    height: 65px;
    padding: 0 8px;
    border-bottom: 2px solid var(--tb-accent-ink);
    white-space: nowrap;
  }
  .tb-up-table td { height: 65px; padding: 0 8px; border-bottom: 1px solid var(--tb-line); }
  .tb-up-table tbody tr { cursor: pointer; }
  .tb-up-table tbody tr:hover { background: var(--tb-accent-soft); }
  .tb-up-table tbody tr[aria-selected="true"] { background: var(--tb-accent-soft); }
  .tb-up-table tbody tr:focus-visible { outline: 2px solid var(--tb-accent); outline-offset: -2px; }
  .tb-up-table tbody tr:last-child td { border-bottom: 2px solid var(--tb-accent-ink); }
  /* Column 1 is the radio, column 2 the trigger glyph — both fixed, per the component sheet. */
  .tb-up-table__pick { width: 42px; padding-left: 32px; }
  .tb-up-table__icon { width: 48px; color: var(--tb-icon); }
  .tb-up-table__name { width: 42%; }
  /* The radio is invisible until the row is hovered or chosen, which is what the sheet shows. */
  .tb-up-radio {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid var(--tb-accent-ink);
    display: inline-block;
    opacity: 0;
  }
  .tb-up-table tbody tr:hover .tb-up-radio { opacity: 1; }
  .tb-up-table tbody tr[aria-selected="true"] .tb-up-radio {
    opacity: 1;
    background: var(--tb-accent-ink);
  }
  .tb-up-pick__foot {
    margin-top: auto;
    padding-top: 26px;
    border-top: 1px solid var(--tb-accent-ink);
    display: flex;
    align-items: center;
    gap: 16px;
  }

  /* ── Review (P4) and the confirmation (P5) ───────────────────────────────────────────────────── */
  .tb-up-review__h { margin: 0 0 8px; font-size: var(--tb-fs); font-weight: var(--tb-fw); }
  .tb-up-review__art { padding: 8px; border-radius: var(--tb-r); background: var(--tb-accent-soft); }
  .tb-up-review__art > div {
    border-radius: var(--tb-r);
    background: var(--tb-bg);
    min-height: 288px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 32px;
  }
  .tb-up-review__art .tb-up-conn { width: 58px; height: 58px; font-size: var(--tb-fs); }
  .tb-up-done {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    text-align: center;
  }
  .tb-up-done p {
    margin: 0;
    max-width: 26ch;
    font-size: var(--tb-fs-lg);
    font-weight: var(--tb-fw);
    line-height: 1.45;
  }

  /* ── The preview-only state switcher. NOT part of the design; it exists so every frame can be
     reached without a backend. Deliberately styled to look like a dev tool, not like the product. ── */
  .tb-up-devbar {
    /* Above the scrim, or the switcher is unreachable the moment a modal state is selected — which is
       exactly what happened the first time this was driven. z-index 51 sits one above .tb-up-scrim. */
    position: relative;
    z-index: 51;
    background: var(--tb-bg);
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 8px 48px;
    border-bottom: 1px dashed var(--tb-line);
    font-size: var(--tb-fs-sm);
    color: var(--tb-muted);
  }
  .tb-up-devbar button {
    font: inherit;
    font-size: var(--tb-fs-sm);
    padding: 4px 8px;
    border: 1px solid var(--tb-line);
    border-radius: 4px;
    background: transparent;
    color: var(--tb-ink);
    cursor: pointer;
  }
  .tb-up-devbar button[aria-pressed="true"] {
    background: var(--tb-accent-soft);
    border-color: var(--tb-accent);
  }

  @media (prefers-reduced-motion: reduce) {
    .tb-up-card, .tb-up-btn, .tb-up__publish { transition: none; }
  }
`;
